import { useState, useRef } from 'react';
import { CheckCircle } from 'lucide-react';
import RichContent from '../content/RichContent';
import { handleSmartPaste } from '../content/sanitizePaste';

/**
 * Merge consecutive 'paragraph' blocks into a single one (joined by <br><br>).
 * Preserves image/renderer/other blocks untouched.
 */
export function mergeBlocks(blocks) {
    if (!blocks || !Array.isArray(blocks)) return blocks;
    const paragraphs = [];
    const others = [];
    for (const b of blocks) {
        if (b.type === 'paragraph' && b.isActive !== false) paragraphs.push(b.content || '');
        else others.push(b);
    }
    if (paragraphs.length <= 1) return blocks;
    return [{ type: 'paragraph', content: paragraphs.join('<br><br>'), isActive: true }, ...others];
}

/**
 * A block editor that shows merged text as rendered content (click to edit).
 * On blur, splits back by "<br><br>" into paragraph blocks and preserves
 * non-paragraph blocks (images, renderers) at the end.
 */
export function EditableBlock({ blocks, onChange }) {
    const [editing, setEditing] = useState(false);
    const ref = useRef(null);
    const merged = mergeBlocks(blocks || []);
    const html = (merged || []).filter(b => b.type === 'paragraph').map(b => b.content || '').join('<br><br>');
    const rendererBlocks = (blocks || []).filter(b => b.type === 'renderer');

    const handleBlur = () => {
        if (ref.current && onChange) {
            const newHtml = ref.current.innerHTML;
            const parts = newHtml.split(/<br\s*\/?>\s*<br\s*\/?>/gi).map(p => p.trim()).filter(Boolean);
            const newBlocks = parts.map(p => ({ type: 'paragraph', content: p, isActive: true }));
            const others = (blocks || []).filter(b => b.type !== 'paragraph');
            onChange([...newBlocks, ...others]);
        }
        setEditing(false);
    };

    if (!editing) {
        return (
            <div className="qu-editable" onClick={() => setEditing(true)} title="Cliquer pour éditer">
                <RichContent blocks={merged} />
            </div>
        );
    }

    return (
        <div className="qu-editable-wrapper">
            <div
                ref={ref}
                className="qu-editable qu-editable--active"
                contentEditable
                suppressContentEditableWarning
                dangerouslySetInnerHTML={{ __html: html }}
                onBlur={handleBlur}
                onPaste={handleSmartPaste}
                onKeyDown={(e) => { if (e.key === 'Escape') e.target.blur(); }}
            />
            {rendererBlocks.length > 0 && (
                <div className="qu-editable-renderers">
                    <RichContent blocks={rendererBlocks} />
                </div>
            )}
        </div>
    );
}

/**
 * Two-column diff for a generic block section (enonce, explication, etc.).
 * Left is read-only render, right is editable.
 */
export function DiffSection({ label, before, after, onChangeAfter }) {
    const beforeBlocks = mergeBlocks(Array.isArray(before) ? before : []);
    const afterBlocks = Array.isArray(after) ? after : [];
    const beforeText = (mergeBlocks(beforeBlocks) || []).map(b => b.content || '').join(' ');
    const afterText = (mergeBlocks(afterBlocks) || []).map(b => b.content || '').join(' ');
    const changed = beforeText !== afterText;

    return (
        <div className={`qu-diff-section ${changed ? 'qu-diff-section--changed' : ''}`}>
            <div className="qu-diff-label">{label} {changed && <span className="qu-diff-badge">modifié</span>}</div>
            <div className="qu-diff-cols">
                <div className="qu-diff-col qu-diff-col--before">
                    <RichContent blocks={mergeBlocks(beforeBlocks)} />
                </div>
                <div className="qu-diff-col qu-diff-col--after">
                    <EditableBlock blocks={afterBlocks} onChange={onChangeAfter} />
                </div>
            </div>
        </div>
    );
}

/**
 * Two-column diff for the list of answers (a/b/c/d...) with correct marker.
 * newBonneReponse falls back to bonneReponse when absent.
 */
export function ResponsesDiff({ before, after, bonneReponse, newBonneReponse, onChangeAfter }) {
    const ids = ['a', 'b', 'c', 'd', 'e', 'f'];
    const beforeMap = {};
    const afterMap = {};
    (Array.isArray(before) ? before : []).forEach(r => { beforeMap[r.id] = r; });
    (Array.isArray(after) ? after : []).forEach(r => { afterMap[r.id] = r; });

    return (
        <div className="qu-diff-section">
            <div className="qu-diff-label">Réponses</div>
            <div className="qu-diff-cols">
                <div className="qu-diff-col qu-diff-col--before">
                    {ids.map(id => {
                        const r = beforeMap[id];
                        if (!r) return null;
                        const isCorrect = bonneReponse === id;
                        return (
                            <div key={id} className={`qu-diff-rep ${isCorrect ? 'qu-diff-rep--correct' : ''}`}>
                                <span className="qu-diff-rep-id">{id.toUpperCase()}.</span>
                                <RichContent blocks={mergeBlocks(r.content || [])} />
                                {isCorrect && <CheckCircle size={12} />}
                            </div>
                        );
                    })}
                </div>
                <div className="qu-diff-col qu-diff-col--after">
                    {ids.map(id => {
                        const r = afterMap[id];
                        if (!r) return null;
                        const isCorrect = (newBonneReponse || bonneReponse) === id;
                        return (
                            <div key={id} className={`qu-diff-rep ${isCorrect ? 'qu-diff-rep--correct' : ''}`}>
                                <span className="qu-diff-rep-id">{id.toUpperCase()}.</span>
                                <EditableBlock
                                    blocks={r.content || []}
                                    onChange={(newContent) => {
                                        if (onChangeAfter) {
                                            const updated = (Array.isArray(after) ? after : []).map(rep =>
                                                rep.id === id ? { ...rep, content: newContent } : rep
                                            );
                                            onChangeAfter(updated);
                                        }
                                    }}
                                />
                                {isCorrect && <CheckCircle size={12} />}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
